import { Card, Heading, Separator, toast } from "@overmap-ai/blocks"
import { ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import {
	APIError,
	selectAttachmentsOfFormSubmissionFieldValues,
	selectFormById,
	selectFormRevisionById,
	selectFormSubmissionById,
} from "@overmap-ai/core"
import { extractFilesAndPromisesFromFieldValues, type FieldValues } from "@overmap-ai/forms"
import { useSyncedState } from "@overmap-ai/hooks"
import { gray } from "@radix-ui/colors"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { FormSubmitter } from "../../components/Form/FormSubmitter"
import {
	useDeserializeFieldSchema,
	useDeserializeFieldValues,
	useHydrateFieldValuesWithAttachments,
} from "../../components/Form/hooks"
import { getAttachmentPayloadsFromFilesAndPromises } from "../../components/Form/utils"
import { LuIconWithWrapper } from "../../components/Icons/LuIconWithWrapper"
import { FormSubmissionIndexLabel } from "../../components/IndexLabels/FormSubmissionIndexLabel"
import { useConfirmSubmitDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectLatestFormSubmissionFieldValuesOfFormSubmission } from "../../store/selectors/forms"

const routeApi = getRouteApi(
	"/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/forms/submissions/$formSubmissionId/edit",
)

export const ProjectEditFormSubmissionView = memo(() => {
	const { formSubmissionId } = routeApi.useParams()

	const formSubmission = useAppSelector(selectFormSubmissionById(formSubmissionId))

	if (!formSubmission) {
		throw new Error(`forms submission with id ${formSubmissionId} not found in store.`)
	}

	const form = useAppSelector(selectFormById(formSubmission.form))

	if (!form) {
		throw new Error(`form with uuid ${formSubmission.uuid}, not found in store.`)
	}

	const formRevision = useAppSelector(selectFormRevisionById(formSubmission.form_revision))

	if (!formRevision) {
		throw new Error(`forms revision with id ${formSubmission.form_revision} not found in store.`)
	}

	const latestFormSubmissionFieldValues = useAppSelector(
		selectLatestFormSubmissionFieldValuesOfFormSubmission(formSubmission.uuid),
	)
	const attachments = useShallowEqaulSelector(
		selectAttachmentsOfFormSubmissionFieldValues(latestFormSubmissionFieldValues?.uuid ?? ""),
	)
	const navigate = useNavigate()
	const openConfirmSubmitDialog = useConfirmSubmitDialog()

	const fieldSchema = useDeserializeFieldSchema(formRevision.fields)
	const fieldValues = useDeserializeFieldValues(fieldSchema, latestFormSubmissionFieldValues?.values)
	const valuesWithAttachments = useHydrateFieldValuesWithAttachments(
		fieldSchema,
		fieldValues,
		attachments,
		sdk.formSubmissionFieldValuesAttachments,
	)

	const [watchedValuesWithAttachments, setWatchedValuesWithAttachments] = useSyncedState(valuesWithAttachments)

	const handleSubmitConfirmed = useCallback(
		(allValues: FieldValues) => {
			const [values, filesAndPromises] = extractFilesAndPromisesFromFieldValues(
				fieldSchema.getFields(),
				allValues,
			)

			const [formSubmissionFieldValues, promise] = sdk.formSubmissionFieldValues.add({
				form_submission: formSubmission.uuid,
				values: fieldSchema.serializeValues(values),
				published_at: new Date().toISOString(),
			})

			if (Object.keys(filesAndPromises).length !== 0) {
				void sdk.formSubmissionFieldValuesAttachments.bulkAdd(
					getAttachmentPayloadsFromFilesAndPromises(
						formSubmissionFieldValues.uuid,
						filesAndPromises,
						attachments,
					),
				)
			}

			promise
				.then(() => {
					toast.success({
						title: "Success",
						description: "Successfully edited submission.",
					})
				})
				.catch((error) => {
					toast.error({
						title: "Failed to edit submission.",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})

			void navigate({
				replace: true,
				from: "/$organizationId/$projectId/forms/submissions/$formSubmissionId/edit",
				to: "/$organizationId/$projectId/forms/submissions/$formSubmissionId",
			})
		},
		[fieldSchema, navigate, formSubmission.uuid, attachments],
	)

	const handleSubmit = useCallback(
		async (allValues: FieldValues) => {
			openConfirmSubmitDialog({
				title: "Edit form submission?",
				description: "You are about to edit the values of this form submission. Are you sure?",
				onAction: () => {
					handleSubmitConfirmed(allValues)
				},
			})
		},
		[handleSubmitConfirmed, openConfirmSubmitDialog],
	)

	const canSubmit = !fieldSchema.areValuesEqual(valuesWithAttachments, watchedValuesWithAttachments)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex justify-between gap-2">
					<div className="flex min-w-0 items-center gap-2">
						<LuIconWithWrapper icon={modelIconMapping.form} color={gray.gray9} background />
						<Heading size="xl" className="truncate">
							{form.name}
						</Heading>
						<FormSubmissionIndexLabel formSubmissionId={formSubmission.uuid} size="xl" accentColor="base" />
					</div>
				</div>

				<Separator size="full" />

				<div className="w-full grow overflow-y-auto overflow-x-hidden">
					<Card size="sm" variant="soft">
						<FormSubmitter
							formRevision={formRevision}
							values={valuesWithAttachments}
							onSubmit={handleSubmit}
							onValuesChange={setWatchedValuesWithAttachments}
							hideSubmit={!canSubmit}
							size="sm"
						/>
					</Card>
				</div>
			</section>
		</ViewContainer>
	)
})
ProjectEditFormSubmissionView.displayName = "ProjectEditFormSubmissionView"
